import {
  keepPreviousData,
  queryOptions,
  type DefaultError,
  type QueryKey,
  type QueryFunction,
  type UndefinedInitialDataOptions,
} from '@tanstack/react-query'

/**
 * Golden path for server-backed IndexDataTable queries.
 *
 * Table search/filter/sort/page changes should keep previous rows on screen
 * during background refetches, but initial-load failures still need to reach the
 * route error boundary. Use this for index/list queryOptions that feed
 * IndexDataTable; override deliberately only for non-table lookups.
 *
 * Route loaders should warm these queries through prefetchIndexTableRouteQuery
 * without awaiting them, so URL-only table changes do not remount the page.
 */
export function indexTableQueryOptions<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TQueryFunction extends QueryFunction<TQueryFnData, TQueryKey> = QueryFunction<
    TQueryFnData,
    TQueryKey
  >,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & {
    queryFn: TQueryFunction
  }
) {
  const { placeholderData = keepPreviousData, throwOnError = true, ...query } = options

  return {
    ...queryOptions({
      ...query,
      placeholderData,
      throwOnError,
    }),
    // Export pagination also calls the transport directly with its narrower context.
    queryFn: query.queryFn,
  }
}

/**
 * Real query clients prefetch; lightweight route-loader test clients may only expose
 * ensureQueryData. Options pass straight through, so the client's own signature checks them.
 */
type IndexTablePrefetchClient<TOptions> =
  | { prefetchQuery(options: TOptions): Promise<unknown> }
  | { ensureQueryData(options: TOptions): Promise<unknown> }

/**
 * Warm URL-backed index data from route loaders without making
 * search/filter/sort changes block the whole page behind the router pending
 * component.
 */
export function prefetchIndexTableRouteQuery<TOptions>(
  queryClient: IndexTablePrefetchClient<NoInfer<TOptions>>,
  options: TOptions
): Promise<unknown> {
  return 'prefetchQuery' in queryClient
    ? queryClient.prefetchQuery(options)
    : queryClient.ensureQueryData(options)
}

/**
 * Every query in one call shares an options type; warm differently typed queries with separate
 * prefetchIndexTableRouteQuery calls.
 */
export function prefetchIndexTableRouteQueries<TOptions>(
  queryClient: IndexTablePrefetchClient<NoInfer<TOptions>>,
  queries: ReadonlyArray<TOptions | null | undefined | false>
): Promise<unknown>[] {
  return queries
    .filter((query): query is TOptions => Boolean(query))
    .map((query) => prefetchIndexTableRouteQuery(queryClient, query))
}
